import { useState, useEffect, useRef } from 'react'
import { LinkButton, InjectedModalProps, useModal } from 'vvs-uikit'
import { useAppDispatch, useAppSelector } from 'state'
import useGotchiOwner from 'state/vvsgotchi/hooks/useGotchiOwner'
import { setOnboarding } from 'state/vvsgotchi'
import { initOnboarding } from 'state/vvsgotchi/models'
import useActiveWeb3React from 'hooks/useActiveWeb3React'
import { useTranslation } from 'contexts/Localization'
import { StepDots } from 'components/StepIndicator'
import { GotchiModal } from 'views/VvsGotchi/components/Modal'
import ActionButton from 'views/VvsGotchi/components/ActionButton'

import { useGameManager } from 'state/vvsgotchi/hooks/useGameManager'
import { playBgm } from 'state/vvsgotchi/bgm'
import { useVvsGotchiAudioModeManager, GOTCHI_DEFAULT_BGM } from 'state/vvsgotchi/hooks/useConfigManager'

import _onboardSteps from './steps'
import { ModalBanner, StepDetailBox } from './styles'

const OnboardingModal: React.FC<InjectedModalProps> = ({ onDismiss }) => {
  const { account } = useActiveWeb3React()
  const { t } = useTranslation()
  const { bgmPlay } = useVvsGotchiAudioModeManager()
  const dispatch = useAppDispatch()
  const onboarding = useAppSelector((state) => state.vvsgotchi.onboarding?.[account] ?? initOnboarding)

  const [step, setStep] = useState(1)

  const onboardSteps = _onboardSteps.slice(onboarding.dontShowAgain ? -1 : 0, _onboardSteps.length)
  const stepDetail = onboardSteps[step - 1]
  const finished = step === onboardSteps.length
  return (
    <GotchiModal
      title={<ModalBanner background={stepDetail.banner} />}
      onDismiss={onDismiss}
      closeIconColor="black"
      hideHeaderPadding
      maxBodyHeight="60vh"
    >
      {onboardSteps.length > 1 && (
        <StepDots steps={onboardSteps.length} currentStep={step} onClick={(s) => setStep(s)} />
      )}
      <StepDetailBox my={4}>{stepDetail.content(t)}</StepDetailBox>
      <ActionButton
        onClick={() => {
          if (finished || onboarding.dontShowAgain) {
            if (bgmPlay) {
              // play immediately inside click event handler,
              // to prevent calling play() in next event loop cycle
              playBgm(GOTCHI_DEFAULT_BGM)
            }
            onDismiss()
          } else {
            setStep(step + 1)
          }
        }}
      >
        {finished ? (bgmPlay ? t('Start Game') : t('Get Started')) : t('Next')}
      </ActionButton>
      {!onboarding.dontShowAgain && (
        <LinkButton
          mt={[2, null, 4]}
          mb={[0, null, 3]}
          onClick={() => {
            dispatch(setOnboarding({ address: account, onboarding: { dontShowAgain: true } }))
            onDismiss()
          }}
        >
          {t("Don't show me again")}
        </LinkButton>
      )}
    </GotchiModal>
  )
}

export const useOnboardingModal = () => {
  const { ready } = useGotchiOwner()
  const { account } = useActiveWeb3React()
  const onboarding = useAppSelector((state) => state.vvsgotchi.onboarding?.[account] ?? initOnboarding)
  const { bgmPlay } = useVvsGotchiAudioModeManager()
  const { isGameStarted, ...gameStartedSwitches } = useGameManager()
  const gameStartedSwitchesRef = useRef(gameStartedSwitches)
  gameStartedSwitchesRef.current = gameStartedSwitches

  const modalSwitches = useModal(
    <OnboardingModal />,
    false,
    true,
    'vvsgotchiOnboarding',
    gameStartedSwitchesRef.current.start,
  )
  const modalSwitchesRef = useRef(modalSwitches)
  modalSwitchesRef.current = modalSwitches

  useEffect(() => {
    const [openModal] = modalSwitchesRef.current
    const { start, reset } = gameStartedSwitchesRef.current

    const openModalOrStartGame = async () => {
      if (ready && !isGameStarted) {
        if (!onboarding.dontShowAgain || bgmPlay) {
          reset()
          openModal()
        } else {
          start()
        }
      }
    }

    openModalOrStartGame()
  }, [ready, isGameStarted, onboarding, bgmPlay])
}
